(function initialize365CollectionLoadMore(root, factory) {
  const api = factory();

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    api.start(root);
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function create365CollectionLoadMore() {
  "use strict";

  const RELEASE_ID = "365nails-collection-pagination-v2";
  const LOAD_MORE_SELECTOR = "[data-collection-load-more]";
  const BUTTON_SELECTOR = "[data-collection-load-more-button]";
  const PRODUCT_SELECTOR = ".product-column[data-collection-product-id]";
  const PRODUCTS_LIST_SELECTOR = "#ProductsList";
  const CATALOG_SELECTOR = "script[data-365-commerce-catalog]";
  const SCHEMA_SELECTOR = "script[data-collection-schema]";

  function productCards(list) {
    return [...(list?.children || [])].filter((child) => child.matches?.(PRODUCT_SELECTOR));
  }

  function productId(card) {
    return String(card?.getAttribute?.("data-collection-product-id") || "").trim();
  }

  function appendUniqueProductCards(currentList, nextList, documentRef) {
    if (!currentList || !nextList || !documentRef) return 0;

    const existingIds = new Set(productCards(currentList).map(productId).filter(Boolean));
    let appended = 0;

    for (const card of productCards(nextList)) {
      const id = productId(card);
      if (!id || existingIds.has(id)) continue;

      const clone = typeof documentRef.importNode === "function"
        ? documentRef.importNode(card, true)
        : card.cloneNode(true);
      currentList.append(clone);
      existingIds.add(id);
      appended += 1;
    }

    return appended;
  }

  function catalogProducts(script) {
    if (!script?.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed?.products) ? parsed.products : [];
    } catch {
      return [];
    }
  }

  function mergeCatalogProducts(currentScript, nextScript) {
    if (!currentScript || !nextScript) return 0;

    const currentProducts = catalogProducts(currentScript);
    const known = new Set(currentProducts.map((product) => String(product?.id || "")));
    const additions = catalogProducts(nextScript).filter((product) => {
      const id = String(product?.id || "");
      if (!id || known.has(id)) return false;
      known.add(id);
      return true;
    });

    if (additions.length > 0) {
      currentScript.textContent = JSON.stringify({
        products: [...currentProducts, ...additions],
      });
    }

    return additions.length;
  }

  function loadedProductCount(list) {
    return productCards(list).length;
  }

  function mergeProductSchema(currentScript, nextScript, displayedList) {
    if (!currentScript?.textContent || !nextScript?.textContent || !displayedList) return 0;

    try {
      const graph = JSON.parse(currentScript.textContent);
      const nextGraph = JSON.parse(nextScript.textContent);
      const current = graph?.["@graph"]?.find((entry) => entry["@type"] === "ItemList");
      const next = nextGraph?.["@graph"]?.find((entry) => entry["@type"] === "ItemList");
      if (!Array.isArray(current?.itemListElement) || !Array.isArray(next?.itemListElement)) return 0;

      const known = new Map();
      for (const item of [...current.itemListElement, ...next.itemListElement]) {
        if (!item?.url) continue;
        const url = new URL(item.url).href;
        if (!known.has(url)) known.set(url, item);
      }
      const baseUrl = current.itemListElement[0]?.url || next.itemListElement[0]?.url;
      const firstPosition = current.itemListElement[0]?.position || next.itemListElement[0]?.position || 1;
      const items = productCards(displayedList).map((card, index) => {
        const relativeUrl = card.getAttribute("data-collection-product-url");
        const item = relativeUrl && known.get(new URL(relativeUrl, baseUrl).href);
        return item ? { ...item, position: firstPosition + index } : null;
      });
      // Never invent schema entries for a card whose source graph is absent or invalid.
      if (items.some((item) => !item)) return 0;
      if (current.numberOfItems === items.length && JSON.stringify(current.itemListElement) === JSON.stringify(items)) return 0;

      const added = items.length - current.itemListElement.length;
      current.numberOfItems = items.length;
      current.itemListElement = items;
      currentScript.textContent = JSON.stringify(graph);
      return added;
    } catch {
      return 0;
    }
  }

  function progressText(loaded, total) {
    return `Showing ${loaded} of ${total} products`;
  }

  function setLoading(button, loading) {
    if (!button) return;
    const label = button.querySelector?.("[data-collection-load-more-label]");
    button.setAttribute("aria-disabled", String(loading));
    button.toggleAttribute("data-loading", loading);
    if (label) label.textContent = loading ? "Loading products…" : "Load more products";
  }

  function updateLoader(loader, nextDocument, loaded, loadedPageUrl) {
    const total = Number(loader.dataset.totalProducts || 0);
    const progress = loader.querySelector("[data-collection-load-more-progress]");
    const button = loader.querySelector(BUTTON_SELECTOR);
    const nextButton = nextDocument.querySelector(BUTTON_SELECTOR);

    if (progress) progress.textContent = progressText(loaded, total);
    if (nextButton && button) {
      const nextHref = nextButton.getAttribute("href");
      button.href = new URL(nextHref, loadedPageUrl).href;
      setLoading(button, false);
      return;
    }

    button?.remove();
  }

  function announce(loader, message) {
    const status = loader?.querySelector?.("[data-collection-load-more-status]");
    if (status) status.textContent = message;
  }

  async function loadNextPage(button, {
    DOMParserImpl,
    documentRef,
    fetchImpl,
    view,
  }) {
    const loader = button.closest(LOAD_MORE_SELECTOR);
    const currentList = documentRef.querySelector(PRODUCTS_LIST_SELECTOR);
    if (!loader || !currentList || button.getAttribute("aria-disabled") === "true") return 0;

    setLoading(button, true);
    loader.removeAttribute("data-load-more-failed");

    try {
      const response = await fetchImpl(button.href, {
        headers: {
          accept: "text/html",
          "x-requested-with": "XMLHttpRequest",
        },
      });
      if (!response?.ok) throw new Error("collection page request failed");

      const nextDocument = new DOMParserImpl().parseFromString(await response.text(), "text/html");
      const nextList = nextDocument.querySelector(PRODUCTS_LIST_SELECTOR);
      if (!nextList) throw new Error("next product grid missing");

      const appended = appendUniqueProductCards(currentList, nextList, documentRef);
      mergeCatalogProducts(
        documentRef.querySelector(CATALOG_SELECTOR),
        nextDocument.querySelector(CATALOG_SELECTOR),
      );
      mergeProductSchema(
        documentRef.querySelector(SCHEMA_SELECTOR),
        nextDocument.querySelector(SCHEMA_SELECTOR),
        currentList,
      );
      const loaded = loadedProductCount(currentList);
      updateLoader(loader, nextDocument, loaded, button.href);
      announce(loader, `${appended} more products loaded. ${progressText(loaded, Number(loader.dataset.totalProducts || 0))}.`);

      currentList.dispatchEvent(new view.CustomEvent("shopify:section:load", {
        bubbles: true,
      }));
      currentList.dispatchEvent(new view.CustomEvent("365:collection:products-appended", {
        bubbles: true,
        detail: { appended, loaded },
      }));

      return appended;
    } catch {
      loader.dataset.loadMoreFailed = "true";
      setLoading(button, false);
      announce(loader, "Products could not be loaded. Use the page links below to continue.");
      return 0;
    }
  }

  function start(view) {
    const documentRef = view.document;
    if (!documentRef || documentRef.documentElement?.dataset.collectionLoadMoreBound === RELEASE_ID) {
      return;
    }

    if (documentRef.documentElement) {
      documentRef.documentElement.dataset.collectionLoadMoreBound = RELEASE_ID;
    }

    documentRef.addEventListener("click", (event) => {
      const button = event.target?.closest?.(BUTTON_SELECTOR);
      if (!button || button.closest(LOAD_MORE_SELECTOR)?.dataset.loadMoreFailed === "true") return;

      event.preventDefault();
      loadNextPage(button, {
        DOMParserImpl: view.DOMParser,
        documentRef,
        fetchImpl: view.fetch.bind(view),
        view,
      });
    });
  }

  return {
    RELEASE_ID,
    appendUniqueProductCards,
    catalogProducts,
    loadedProductCount,
    loadNextPage,
    mergeCatalogProducts,
    mergeProductSchema,
    productCards,
    progressText,
    start,
  };
});
